import type { ReactNode } from 'react'
import { ImageIcon } from 'lucide-react'
import { objectPositionOf, type FocalPoint } from '@lib/helpers/focal-point'
import { cn } from '@/lib/utils'
import { Facts, type Fact } from '@/components/ui/facts'


interface RecordHeaderProps {
  /** What kind of record this is — "Event", "Venue". Set as the eyebrow. */
  kind: string
  /** The record's status, beside the kind: a `StatusBadge`, usually. */
  badge?: ReactNode
  title: ReactNode
  /** The record's photograph, or null. */
  src: string | null
  focalPoint?: FocalPoint | null | undefined
  /**
   * Venue, date, guests: each behind its icon, in a slot that stays when the
   * record has no value for it (see `Facts`).
   */
  facts?: Fact[]
  /** The page's actions: the record's own buttons, beside its name. */
  actions?: ReactNode
  className?: string
}

/**
 * The header of a record page, on Plan View's event-header recipe: a compact
 * card with the photograph framed at 4:3 on the left — evidence, not wallpaper —
 * and the name, its kind and its facts beside it, never on it. The actions sit at
 * the end of the same card.
 *
 * It replaced a full-bleed photograph with the title printed across it, which
 * made every record's first screen a picture and made the name only as legible as
 * the scrim under it. A record with no photograph shows the empty well on the
 * plan grid rather than a stock picture that could be taken for the venue's own.
 */
export function RecordHeader({ kind, badge, title, src, focalPoint, facts, actions, className }: RecordHeaderProps) {
  return (
    <header
      className={cn(
        'grid grid-cols-[96px_minmax(0,1fr)] items-center gap-4 rounded-lg border border-line bg-surface p-4',
        'sm:grid-cols-[152px_minmax(0,1fr)_auto] sm:gap-5',
        className,
      )}
    >
      <div
        className={cn(
          'relative grid aspect-[4/3] place-items-center overflow-hidden rounded-md bg-surface-sunken text-ink-subtle',
          'after:pointer-events-none after:absolute after:inset-0 after:rounded-md after:shadow-[var(--ds-media-ring)]',
          src === null ? 'plan-grid' : undefined,
        )}
      >
        {src !== null ? (
          <img
            src={src}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            style={{ objectPosition: objectPositionOf(focalPoint ?? null, '50% 40%') }}
          />
        ) : (
          <span className="grid justify-items-center gap-1 text-small text-ink-muted">
            <ImageIcon aria-hidden className="h-5 w-5 text-ink-subtle" />
            <span className="hidden sm:inline">No photo yet</span>
          </span>
        )}
      </div>

      <div className="min-w-0">
        <div className="mb-2 flex flex-wrap items-center gap-2.5">
          <span className="font-eyebrow">{kind}</span>
          {badge}
        </div>
        <h1 className="m-0 min-w-0 wrap-anywhere text-title font-semibold text-ink [text-wrap:balance]">
          {title}
        </h1>
        {facts && facts.length > 0 ? <Facts facts={facts} className="mt-3 [&_svg]:h-4 [&_svg]:w-4" /> : null}
      </div>

      {actions ? (
        <div className="col-span-full flex flex-wrap items-center gap-2 self-start sm:col-span-1">{actions}</div>
      ) : null}
    </header>
  )
}
